import { ERROR_CODES, type ErrorCode } from '@badger-sentinel/protocol';

/**
 * One title per `ErrorCode`, because the enum is not copy.
 *
 * `ErrorNote` used to fall back to the code itself and, of four call sites in
 * the app, exactly one passed a title — so players read `UNAUTHORIZED`,
 * `SEAT_TAKEN`, `RATE_LIMITED` and `INTERNAL` as headings, and the console's
 * alert lane built its own titles the same way (`console/model/alerts.ts`).
 *
 * **Titles only.** The copy rules give an error one uppercase condition title
 * and at most one short actionable sentence; the title is the client's, because
 * it is a fixed vocabulary, and the sentence stays the server's, because it is
 * the half that carries the seconds left in a bucket or which turn has not
 * resolved yet. A server sentence that stated the condition instead of an
 * action was fixed where it was written, not papered over here.
 *
 * A code with no entry is a protocol that has moved under this bundle. It gets
 * `ERROR`, which is honest, and never the raw enum.
 */
const ERROR_TITLES: Readonly<Record<ErrorCode, string>> = Object.freeze({
  BAD_REQUEST: 'CHECK THE ENTRY',
  UNAUTHORIZED: 'NOT RECOGNISED',
  FORBIDDEN: 'NOT PERMITTED',
  NOT_FOUND: 'NOT FOUND',
  SEAT_TAKEN: 'SEAT TAKEN',
  WINDOW_CLOSED: 'WINDOW CLOSED',
  MW_TRACKS_OPEN: 'TRACKS OPEN',
  RATE_LIMITED: 'TOO MANY ATTEMPTS',
  PAYLOAD_TOO_LARGE: 'TOO MUCH DATA',
  ORIGIN_REJECTED: 'ORIGIN REFUSED',
  UNSUPPORTED_PROTOCOL_VERSION: 'SERVER UPDATED',
  INTERNAL: 'SERVER ERROR',
});

/**
 * `NETWORK` is the client's own — `fetch` rejected and there is no server code
 * to carry. It belongs in the same vocabulary as the rest.
 */
const CLIENT_TITLES: Readonly<Record<string, string>> = Object.freeze({
  NETWORK: 'NO CONNECTION',
});

export function errorTitle(code: string | null | undefined): string {
  if (code === null || code === undefined) return 'ERROR';
  if ((ERROR_CODES as readonly string[]).includes(code)) {
    return ERROR_TITLES[code as ErrorCode];
  }
  return CLIENT_TITLES[code] ?? 'ERROR';
}
